import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { KohaAuthService } from '../services/koha-auth.service';
import { parseKohaError } from '../models/koha-error.model';

/**
 * HTTP Interceptor for Koha REST API authentication and error handling.
 *
 * Responsibilities:
 * 1. Attach `Authorization` header (Basic Auth via KohaAuthService) to all /api/ requests
 * 2. Set `Accept: application/json` and `Accept-Language: ar` headers
 * 3. On 401: invalidate credentials, re-attach, retry the request ONCE
 * 4. Parse Koha error responses into typed KohaApiError objects
 *
 * This interceptor does NOT handle:
 * - x-koha-library header (left to Koha's default = patron's home library)
 * - OAuth2 token refresh (not yet enabled on server)
 *
 * All feature services use Angular HttpClient, which flows through this
 * interceptor automatically — they never touch auth directly.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Only intercept API requests
  if (!req.url.startsWith('/api/')) {
    return next(req);
  }

  const authService = inject(KohaAuthService);

  // Attach auth + common headers
  const apiReq = addAuthHeaders(req, authService);

  return next(apiReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // 401 → invalidate credentials, retry exactly once
      if (error.status === 401) {
        console.warn('[AuthInterceptor] 401 received — retrying with fresh credentials');
        authService.invalidate();

        const retryReq = addAuthHeaders(req, authService);
        return next(retryReq).pipe(
          catchError((retryError: HttpErrorResponse) => {
            // Second 401 = credentials are genuinely wrong — don't loop
            const parsed = parseKohaError(retryError.status, retryError.error);
            console.error('[AuthInterceptor] Retry failed:', parsed.type, parsed.error);
            return throwError(() => parsed);
          })
        );
      }

      // All other errors: parse into structured KohaApiError
      const parsed = parseKohaError(error.status, error.error);

      if (error.status === 403) {
        console.error('[AuthInterceptor] Permission denied:', parsed.error);
      }

      return throwError(() => parsed);
    })
  );
};

/**
 * Clone a request with auth and common headers attached.
 */
function addAuthHeaders(req: HttpRequest<unknown>, authService: KohaAuthService): HttpRequest<unknown> {
  const setHeaders: any = {
    'Accept-Language': 'ar',
  };

  // Only set Accept if the request doesn't explicitly provide one
  if (!req.headers.has('Accept')) {
    setHeaders['Accept'] = 'application/json';
  }

  // Only add staff auth if the request doesn't already have an Authorization header
  // (e.g. PatronAuthService provides its own header for login)
  if (!req.headers.has('Authorization')) {
    setHeaders['Authorization'] = authService.getAuthorizationHeader();
  }

  return req.clone({ setHeaders });
}

